import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container } from 'react-bootstrap';
import { IconType } from 'react-icons';
import {
  FiArrowRight,
  FiBriefcase,
  FiGitBranch,
  FiHome,
  FiLayers,
  FiRefreshCw,
  FiTag,
  FiTrash2,
  FiUsers,
  FiBookmark,
} from 'react-icons/fi';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/useToast';
import BasePage from '../../components/BasePage';
import { clearAppCache, hardReload } from '../../utils/clearCache';

import { ActionCard, OptionCard, OptionsGrid, SectionTitle } from './styles';

type Tone = 'green' | 'orange';

interface IOption {
  to: string;
  title: string;
  description: string;
  icon: IconType;
  tone: Tone;
  adminOnly?: boolean;
}

const OPTIONS: IOption[] = [
  {
    to: '/settings/unions',
    title: 'Sindicatos',
    description: 'Gerencie os sindicatos cadastrados',
    icon: FiHome,
    tone: 'green',
    adminOnly: true,
  },
  {
    to: '/settings/companies',
    title: 'Empresas',
    description: 'Gerencie as empresas registradas',
    icon: FiBriefcase,
    tone: 'orange',
  },
  {
    to: '/settings/users',
    title: 'Usuários',
    description: 'Administre acessos e permissões',
    icon: FiUsers,
    tone: 'green',
  },
  {
    to: '/settings/news/categories',
    title: 'Categorias de Notícias',
    description: 'Organize os tópicos de publicações',
    icon: FiBookmark,
    tone: 'orange',
    adminOnly: true,
  },
  {
    to: '/settings/unions/partners-categories',
    title: 'Categorias de Convênios',
    description: 'Classifique os parceiros oferecidos',
    icon: FiTag,
    tone: 'green',
    adminOnly: true,
  },
  {
    to: '/settings/workers-segments',
    title: 'Segmentos de Trabalhadores',
    description: 'Defina agrupamentos e filtros',
    icon: FiLayers,
    tone: 'orange',
    adminOnly: true,
  },
  {
    to: '/settings/foward-complaint',
    title: 'Redirecionamento de denúncias',
    description: 'Configure o roteamento automático',
    icon: FiGitBranch,
    tone: 'green',
    adminOnly: true,
  },
];

const Config: React.FC = () => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    document.title = 'Configurações - Viva Voz - Rede Suco de Laranja';
  }, []);

  const options = useMemo(
    () => OPTIONS.filter(o => !o.adminOnly || user.role === 'admin'),
    [user.role],
  );

  const handleClearCache = useCallback(async () => {
    const ok = window.confirm(
      'Isso vai limpar o cache do navegador e recarregar o painel. Deseja continuar?',
    );
    if (!ok) return;

    setClearing(true);
    try {
      await clearAppCache();
      addToast({
        title: 'Cache limpo',
        body: 'Recarregando a página...',
      });
      setTimeout(() => hardReload(), 400);
    } catch {
      setClearing(false);
      addToast({
        title: 'Erro',
        body: 'Não foi possível limpar o cache.',
      });
    }
  }, [addToast]);

  return (
    <BasePage title="Configurações">
      <Container fluid>
        <OptionsGrid>
          {options.map(option => {
            const Icon = option.icon;
            return (
              <OptionCard key={option.to} to={option.to} $tone={option.tone}>
                <span className="icon">
                  <Icon size={20} />
                </span>
                <div className="content">
                  <span className="title">{option.title}</span>
                  <span className="description">{option.description}</span>
                </div>
                <span className="arrow">
                  <FiArrowRight size={18} />
                </span>
              </OptionCard>
            );
          })}
        </OptionsGrid>

        <SectionTitle>Manutenção</SectionTitle>
        <OptionsGrid>
          <ActionCard
            type="button"
            $tone="orange"
            onClick={handleClearCache}
            disabled={clearing}
          >
            <span className="icon">
              <FiTrash2 size={20} />
            </span>
            <div className="content">
              <span className="title">Limpar cache</span>
              <span className="description">
                Força a busca da versão mais recente do painel.
              </span>
            </div>
            <span className="arrow">
              <FiRefreshCw size={18} />
            </span>
          </ActionCard>
        </OptionsGrid>
      </Container>
    </BasePage>
  );
};

export default Config;
